import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { pushModal, popModal, isTopmostModal } from '@/lib/modalStack';
import { PopupCard } from './PopupCard';
import type { SitePopup } from '@/services/popupsApi';

// `hidden` keeps the notice (and its checkbox) mounted while another dialog is
// on top; the modal behaviour below runs only while it is visible.
export function PopupDialog({ popup, onClose, preview = false, hidden = false }: { popup: SitePopup; onClose: (remember: boolean) => void; preview?: boolean; hidden?: boolean }) {
  const panel = useRef<HTMLDivElement>(null);
  const rememberRef = useRef(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const opener = useRef<HTMLElement | null>(null);
  const unmounting = useRef(false);
  const pressedBackdrop = useRef(false);
  useEffect(() => {
    unmounting.current = false;
    return () => { unmounting.current = true; };
  }, []);
  useEffect(() => {
    if (hidden) return;
    const id = Symbol('site-popup');
    pushModal(id);
    if (!opener.current && document.activeElement instanceof HTMLElement) opener.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLElement>('button')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (!isTopmostModal(id)) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeRef.current(rememberRef.current); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled)') || []);
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || !panel.current?.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && (document.activeElement === last || !panel.current?.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      popModal(id); document.removeEventListener('keydown', keydown); document.body.style.overflow = previousOverflow;
      // Only a real close returns focus; hiding must leave it with the dialog that opened on top.
      if (unmounting.current && opener.current?.isConnected) opener.current.focus();
    };
  }, [hidden]);
  // A drag that starts in the panel (e.g. selecting text) and ends on the backdrop is not a backdrop click.
  return createPortal(<div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-3 sm:p-5" style={hidden ? { display: 'none' } : undefined}
    onPointerDown={event => { pressedBackdrop.current = event.target === event.currentTarget; }}
    onClick={event => { if (event.target === event.currentTarget && pressedBackdrop.current) onClose(rememberRef.current); }}>
    <div ref={panel} role="dialog" aria-modal="true" aria-label={`${popup.title} ${popup.highlight}`} data-site-popup="true" className="max-h-[90dvh] w-full max-w-[480px] overflow-y-auto rounded-3xl"><PopupCard popup={popup} onClose={onClose} onRememberChange={value => { rememberRef.current = value; }} preview={preview} /></div>
  </div>, document.body);
}
